import { ASC, DESC } from '../constants/constants';

/* eslint unknown-word: 0 */
let lastKey = 0;

export function newKey(prefix) {
	lastKey += 1;
	return `${prefix}${lastKey}`;
}

// for testing purpose
export function resetTest() {
	lastKey = 0;
}

export function getSiteId(rData) {
	return (rData.parameterMap && rData.parameterMap.siteId) || rData.siteId;
}

export function getDeviceType(rData) {
	return (
		(rData.parameterMap && rData.parameterMap.deviceType) ||
		rData.deviceType
	);
}

export function getAccountId(rData) {
	return (
		(rData.parameterMap && rData.parameterMap.accountId) || rData.accountId
	);
}

/**
 *
 * @param {Array} paymentsArray List of all available LOAN, LEASE payment options
 * @param {Array} keys List of payment object properties by which to sort
 * @param {Array} orders Order in which to sort the payment object to their respective properties, asc is default
 * @return {Object} Selected LOAN, LEASE payment object
 */
export const getDisplayPayments = (paymentsArray, keys = [], orders = []) => {
	/**
	 * Sort the payment object based on the list of properties in the `keys` argument.
	 * The list is maintained if the sort is based on a missing property
	 */
	const sortedPaymentsArray = [...paymentsArray].sort(
		(paymentOne, paymentTwo) => {
			const sortConditions = keys.map((key, index) => {
				const order = orders[index] ? orders[index] : ASC;
				let criterea = 0;
				switch (order) {
					case DESC:
						criterea = paymentTwo[key] - paymentOne[key];
						break;

					case ASC:
					default:
						criterea = paymentOne[key] - paymentTwo[key];
						break;
				}
				return criterea;
			});
			return sortConditions.reduce(
				(result, condition) => result || condition
			);
		}
	);

	/* Select the first LOAN LEASE Payment in the list */
	return sortedPaymentsArray.reduce((paymentObj, payment) => {
		const { financeType = '' } = payment;
		if (!paymentObj[financeType]) {
			return {
				...paymentObj,
				[financeType]: payment
			};
		}
		return paymentObj;
	}, {});
};

export function replacePlaceholders(
	labelWithPlaceholder,
	placeholderValuesObj
) {
	// global and ignore case flags which permits replace() to replace each occurrence of matched items
	// ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/replace

	if (
		!placeholderValuesObj ||
		Object.entries(placeholderValuesObj).length === 0
	) {
		if (
			!labelWithPlaceholder ||
			Object.entries(labelWithPlaceholder).length === 0
		) {
			return 'Invalid Input';
		}
		return labelWithPlaceholder;
	}

	const regex = new RegExp(Object.keys(placeholderValuesObj).join('|'), 'gi');

	return labelWithPlaceholder.replace(regex, matched => {
		return placeholderValuesObj[matched];
	});
}

function numberCheck(num) {
	return typeof num === 'number' && Number.isFinite(num);
}

export function isNumbers(nums) {
	if (!nums || !Array.isArray(nums)) {
		return false;
	}

	return nums.every(numberCheck);
}

// Logic is copied from PaymentCalculaterTool.java in cms project

// export function getMonthlyPayment(price, downPayment,interestRate,numberOfMonths) {
// 	if (price && downPayment && interestRate && numberOfMonths && isNumbers([price, downPayment,interestRate,numberOfMonths])) {
// 		return Math.round(((price - downPayment) * (interestRate / 100 / 12)) / (1 - (1 + interestRate / 100 / 12) ** (-numberOfMonths)));
// 	}
// 	return '';

// }

export function getMonthlyPaymentPerThousand(monthlyPayment, amountFinanced) {
	if (isNumbers([amountFinanced, monthlyPayment]) && amountFinanced > 0) {
		return monthlyPayment / (amountFinanced / 1000.0);
	}

	return '';
}

export function getTotalMonthlyPayments(monthlyPayment, term) {
	if (isNumbers([term, monthlyPayment])) {
		return monthlyPayment * term;
	}

	return '';
}

export function getAdjustedDueAtSigning(dueAtSigning, rebate) {
	if (isNumbers([dueAtSigning, rebate]) && dueAtSigning - rebate > 0) {
		return dueAtSigning - rebate;
	} else {
		return 0;
	}
}

export function getDisclaimerPlaceholderObj(paymentInfo) {
	if (paymentInfo._type === 'LOAN') {
		const {
			apr,
			dealershipName,
			downPayment,
			lenderLegalName,
			loanCreditScore,
			monthlyPaymentPerThousand,
			loanProgramEndDate,
			term
		} = paymentInfo;

		return {
			'<%=loanCreditScore%>': loanCreditScore,
			'<%=APR%>': apr,
			'<%=term%>': term,
			'<%=monthlyPaymentPerThousand%>': monthlyPaymentPerThousand,
			'<%=downPayment%>': downPayment,
			'<%=lenderLegalName%>': lenderLegalName,
			'<%=loanProgramEndDate%>': loanProgramEndDate,
			'<%=dealershipName%>': dealershipName
		};
	} else if (paymentInfo._type === 'LEASE') {
		const {
			leaseCreditScore,
			leaseLenderLegalName,
			leaseProgramEndDate,
			leaseTerm,
			leaseDueAtSigning,
			acquisitionFee,
			capCostTotal,
			totalMonthlyPayments,
			capCostReduction,
			leaseSecurityDeposit,
			monthlyPayment,
			excessMileCharge,
			annualMiles,
			dealershipName
		} = paymentInfo;

		return {
			'<%=leaseCreditScore%>': leaseCreditScore,
			'<%=leaseLenderLegalName%>': leaseLenderLegalName,
			'<%=leaseProgramEndDate%>': leaseProgramEndDate,
			'<%=leaseTerm%>': leaseTerm,
			'<%=leaseDueAtSigning%>': leaseDueAtSigning,
			'<%=acquisitionFee%>': acquisitionFee,
			'<%=capCostTotal%>': capCostTotal,
			'<%=totalMonthlyPayments%>': totalMonthlyPayments,
			'<%=capCostReduction%>': capCostReduction,
			'<%=leaseSecurityDeposit%>': leaseSecurityDeposit,
			'<%=monthlyPayment%>': monthlyPayment,
			'<%=excessMileCharge%>': excessMileCharge,
			'<%=annualMiles%>': annualMiles,
			'<%=dealershipName%>': dealershipName
		};
	}
	return {};
}

// filter out final price items that do not match the priceType
// this data is consumed by the PricingSummary component
export function filterSummary(summaryItems, priceType) {
	return summaryItems.filter(item => {
		return (
			(item &&
				item.priceType &&
				item.priceType === priceType.toUpperCase()) ||
			!item.priceType
		);
	});
}

// filters out the final price from the summary object
// this data is consumed by the PricingSummary component
export function filterFinal(summaryItems) {
	return summaryItems.filter(item => item.isFinalPrice === true);
}

// filters out the loan and lease from the summary object
// this data is consumed by the PricingLoanLease component
export function filterLoanLease(summaryItems) {
	return summaryItems.filter(
		item => item._type === 'LOAN' || item._type === 'LEASE'
	);
}

export function extractEPriceData(ePriceDetail) {
	const ePriceIndex = ePriceDetail.findIndex(
		element => element.priceType === 'EPRICE'
	);
	// ePrice difference should be placed before ePrice value per Rules Provider
	if (ePriceIndex > 0) {
		const ePriceObj = ePriceDetail[ePriceIndex];
		const ePriceValue = ePriceObj && ePriceObj.formatted;
		let ePriceDifference;
		const ePricePrevIndex = ePriceIndex - 1;
		if (ePriceDetail[ePricePrevIndex]._type === '-') {
			ePriceDifference = ePriceDetail[ePricePrevIndex].formatted;
			return { ePriceValue, ePriceDifference };
		} else if (ePriceValue) {
			return { ePriceValue };
		}
	}

	return {};
}

export function parseEPriceButtonLabel(dPriceArr) {
	const ePriceLabelArr =
		dPriceArr.filter(item => item._type === 'SHOWEPRICE')[0] || {};
	return ePriceLabelArr.label || 'Show ePrice!';
}

export function titleCase(str, sep) {
	return str
		.toLowerCase()
		.split(sep)
		.map(word => {
			return word.charAt(0).toUpperCase() + word.slice(1);
		})
		.join('');
}

export function getDOMContainer(doc, cssClass) {
	const [container] = doc.querySelectorAll(cssClass);
	return container;
}

/**
 * Filters undefined, null or empty strings as well as boolean false from a list of comma separated items, joins them and returns a string
 * Returns null if 'classes' is empty
 * @param {object} classes - An arguments object
 * From Tim Burgher in vehicle title's utility file
 */
export const setClassNames = (...classes) => {
	return [...classes].filter(className => className).join(' ') || null;
};

/**
 * Filters list of cookies down to the cookie with name matching cookieName param. if it exists, and returns
 * its value, if it is defined, or an empty string otherwise.
 *
 * @param cookies
 * @param cookieName
 * @returns {String} cookie name if the cookie exists, or empty string.
 */
export const getCookieValue = (cookieName = '', cookies = []) => {
	return cookies
		.filter(cookie => cookie.name === cookieName)
		.reduce((value, cookie) => cookie.value || value, '');
};

/**
 * Returns promo code from the 'affiliate_promotions' url parameter if it exists
 *
 * @param parameterMap
 * @param parameterName
 * @returns {String} parameter value if the url parameter equal to parameterName exists, or empty string.
 */
export const getParameterValue = (parameterName = '', parameterMap = {}) =>
	parameterMap[parameterName] || '';

/**
 * Returns affiliate offer promo code if one exists in the url parameter 'affiliate_promotions'
 * or in the 'DDC.Affiliate.PromoCode' cookie.
 * Prioritizes url parameter over cookie.
 *
 * @param rData
 * @returns {String} Promo code, if one exists, or empty string
 */
export const getAffiliatePromotion = (rData = {}) => {
	return (
		getParameterValue('affiliate_promotions', rData.parameterMap) ||
		getCookieValue('DDC.Affiliate.PromoCode', rData.cookies)
	);
};



// WEBPACK FOOTER //
// ./src/utils/utility.js